import { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from './api';

// Order statuses come from the backend (GET /api/order-statuses, defined once in
// public/orderStatuses.js), so a status added or renamed there reaches every
// dropdown and badge without another frontend edit. FALLBACK_STATUSES is only
// used until the request returns (or if it fails) so the screen is never empty.
const FALLBACK_STATUSES = [
  { name: 'New Order', color: 'primary', customerEditable: true, approved: false },
  { name: 'Awaiting Action', color: 'danger', customerEditable: false, approved: true },
  { name: 'Confirmed', color: 'info', customerEditable: false, approved: true },
  { name: 'In Production', color: 'warning', customerEditable: true, approved: true },
  { name: 'Ready to Ship', color: 'info', customerEditable: false, approved: true },
  { name: 'Shipped', color: 'secondary', customerEditable: false, approved: true },
  { name: 'Delivered', color: 'success', customerEditable: false, approved: true },
  { name: 'Deposit Paid', color: 'warning', customerEditable: false, approved: true },
  { name: 'Paid in Full', color: 'success', customerEditable: false, approved: true },
  { name: 'Cancelled', color: 'danger', customerEditable: false, approved: true },
];

let cache = null; // resolved list, shared by every component
let inflight = null;
const listeners = new Set();

function load() {
  if (!inflight) {
    inflight = axios.get(`${API_BASE_URL}/api/order-statuses`)
      .then((res) => {
        const list = res.data && Array.isArray(res.data.statuses) && res.data.statuses.length ? res.data.statuses : null;
        if (list) {
          cache = list;
          listeners.forEach((fn) => fn(list));
        }
      })
      .catch(() => { inflight = null; }); // keep the fallback; retry on the next mount
  }
  return inflight;
}

export const getOrderStatuses = () => cache || FALLBACK_STATUSES;

// React hook: the status list (objects), refreshed once the backend answers.
export function useOrderStatuses() {
  const [statuses, setStatuses] = useState(getOrderStatuses);
  useEffect(() => {
    listeners.add(setStatuses);
    load();
    return () => { listeners.delete(setStatuses); };
  }, []);
  return statuses;
}

// Badge colour for a status. Plain function so it also works in the existing
// switch-style helpers; unknown statuses get the neutral colour.
export const statusColor = (name) => {
  const found = getOrderStatuses().find((s) => s.name === name);
  return found ? found.color : 'secondary';
};

// Customer portal rules, from the same list.
export const isCustomerEditableStatus = (name) => {
  const found = getOrderStatuses().find((s) => s.name === name);
  return !!(found && found.customerEditable);
};
export const isApprovedStatus = (name) => {
  const found = getOrderStatuses().find((s) => s.name === name);
  return found ? found.approved : true;
};
